import React from "react";
import { Tooltip, useTheme } from "@mui/material";
import { FiAward, FiPackage, FiZap } from "react-icons/fi";
import { ErrorBox, StatusBadge } from "../organisation/OrgModal";
import { Pill, Skeleton } from "./subscriptionUi";
import {
  formatCents,
  formatDate,
  hasCustomLimits,
  monthlyCents,
  planDisplayName,
  planOf,
  PLAN_STATE,
  planState,
  planStatusKey,
  priceLabel,
  trialDaysLeft,
} from "../../utils/subscriptionData";

const ACCESS_COPY = {
  [PLAN_STATE.PAST_DUE]: "Your last payment failed. Everything keeps working while Stripe retries. Update your payment method to avoid an interruption.",
  [PLAN_STATE.ENDING]: "Your plan is cancelled and runs until the date below. You keep full access until then.",
  [PLAN_STATE.LEGACY]: "This account isn't on a plan yet.",
};

// Days left in the trial as a ring: the arc is the share of the trial still ahead, the number sits in the middle.
const TrialRing = ({ daysLeft, total, percent, color, track }) => {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className="flex shrink-0 items-center gap-3">
      <div
        className="relative h-[3.25rem] w-[3.25rem]"
        role="progressbar"
        aria-label="Trial time left"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={daysLeft}
        aria-valuetext={`${daysLeft} of ${total} days left`}
      >
        <svg viewBox="0 0 48 48" className="h-full w-full -rotate-90" aria-hidden="true">
          <circle cx="24" cy="24" r={radius} fill="none" stroke={track} strokeWidth="5" />
          <circle
            cx="24"
            cy="24"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - percent / 100)}
            className="transition-all duration-500"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-base font-bold leading-none" style={{ color }}>
          {daysLeft}
        </span>
      </div>
      <p className="text-xs leading-tight">
        <span className="block text-sm font-semibold">{daysLeft === 1 ? "day" : "days"} left</span>
        <span className="text-accent-gray-light">of a {total}-day trial</span>
      </p>
    </div>
  );
};

const FACT_TONES = { info: "bg-blue-100 text-blue-900", warn: "bg-amber-100 text-amber-900" };
const DAY_MS = 86400000;

// The Current subscription card: plan, status, price, the date that matters, bought add-ons and the trial
// countdown. `children` are the actions on the right (none when the card is read-only).
//   plans   public plans (GET /plans), for the price of a paid plan
//   addons  the organisation's add-ons; the live ones show next to the plan name
const CurrentPlanCard = ({ plan, addons = [], plans = [], loading = false, error = "", children }) => {
  const theme = useTheme();
  const state = planState(plan);
  const row = planOf(plan);
  const hasPlan = state !== PLAN_STATE.NONE && state !== PLAN_STATE.LEGACY;
  const paid = hasPlan && state !== PLAN_STATE.TRIAL;
  const currentPublic = paid ? plans.find((item) => item.plan_id === row?.plan_id) || null : null;

  const priceLine =
    state === PLAN_STATE.TRIAL ? "Free while your trial lasts" : paid ? (currentPublic ? priceLabel(currentPublic) : "Custom pricing") : "No active subscription";

  // What the next renewal costs: the plan plus monthly add-ons that keep renewing. One-time add-ons were paid
  // once and removed ones stop at the end of the period. Unknown prices leave the total out.
  const planCents = monthlyCents(currentPublic);
  const renewingAddons = addons.filter((addon) => addon.status === "active" && addon.billing === "recurring");
  const addonCents = renewingAddons.reduce((sum, addon) => {
    const unit = currentPublic?.addons?.[addon.addon_type]?.amount_cents;
    return sum + (typeof unit === "number" ? unit * (Number(addon.quantity) || 0) : NaN);
  }, 0);
  const showTotal = paid && planCents !== null && renewingAddons.length > 0 && Number.isFinite(addonCents);
  const totalBreakdown = showTotal ? `${formatCents(planCents)} plan + ${formatCents(addonCents)} add-ons` : "";

  // Trial progress: how much of the trial is left, as a share of its full length
  const daysLeft = state === PLAN_STATE.TRIAL ? trialDaysLeft(plan) : null;
  const trialTotal =
    state === PLAN_STATE.TRIAL && plan?.start_at && plan?.end_at
      ? Math.max(1, Math.round((new Date(plan.end_at).getTime() - new Date(plan.start_at).getTime()) / DAY_MS))
      : null;
  const trialPercent = trialTotal && daysLeft !== null ? Math.min(100, Math.max(0, Math.round((daysLeft / trialTotal) * 100))) : 0;
  const trialColor = daysLeft !== null && daysLeft <= 3 ? "#b40909" : daysLeft !== null && daysLeft <= 7 && trialPercent < 60 ? "#c27c00" : theme.palette.info.main;

  // Each fact is { text, tone }; tone highlights the date that matters (renewal, end of plan, payment due)
  const dateFact = (label, value, tone) => ({ text: `${label} ${formatDate(value)}`, tone });
  const facts =
    state === PLAN_STATE.TRIAL
      ? [{ text: priceLine }, { text: `Started ${formatDate(plan.start_at)}` }, dateFact("Ends", plan.end_at, "info")]
      : paid
        ? [
            showTotal ? { text: `${formatCents(planCents + addonCents)} / month with add-ons`, hint: totalBreakdown } : { text: priceLine },
            { text: plan.billing_cycle === "yearly" ? "Yearly" : "Monthly" },
            state === PLAN_STATE.ENDING
              ? dateFact("Ends", plan.end_at, "warn")
              : state === PLAN_STATE.PAST_DUE
                ? dateFact("Payment due since", plan.current_period_end, "warn")
                : dateFact("Ends", plan.current_period_end, "info"),
          ]
        : state === PLAN_STATE.NONE && row
          ? [dateFact("Ended", row.end_at, "warn")]
          : [{ text: priceLine }];

  // The same add-on bought twice is one line ("Extra panelist seat ×2"); the list is shown on hover or focus
  const addonLines = Object.values(
    addons
      .filter((addon) => addon.status === "active" || addon.status === "canceling")
      .reduce((groups, addon) => {
        const key = addon.addon_type || addon.name;
        const group = groups[key] || { key, name: addon.name, quantity: 0, removing: true };
        group.quantity += Number(addon.quantity) || 0;
        group.removing = group.removing && addon.status === "canceling";
        return { ...groups, [key]: group };
      }, {})
  );
  const addonSummary = addonLines.map((line) => `${line.name} ×${line.quantity}${line.removing ? " (removed, still counts)" : ""}`);
  const attention = ACCESS_COPY[state] || "";

  return (
    <section
      aria-label="Current subscription"
      className="relative overflow-hidden rounded-xl border bg-gradient-to-r from-[#eef3fb] via-white to-white"
      style={{ borderColor: theme.palette.secondary.v4 }}
    >
      <span className="absolute inset-y-0 left-0 w-1 bg-accent-blue" aria-hidden="true" />
      <div className="flex min-h-[4.5rem] flex-wrap items-center gap-x-5 gap-y-3 py-3 pl-5 pr-4">
        {loading ? (
          <div role="status" aria-label="Loading subscription" className="w-full max-w-sm space-y-2">
            <Skeleton className="h-6 w-44" />
            <Skeleton className="h-3 w-72" />
          </div>
        ) : (
          <>
            <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-blue text-white shadow-sm sm:flex" aria-hidden="true">
              {state === PLAN_STATE.TRIAL ? <FiZap className="h-5 w-5" /> : <FiAward className="h-5 w-5" />}
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-accent-gray-light">Current subscription</p>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-xl font-semibold leading-tight">{state === PLAN_STATE.LEGACY ? "No plan yet" : planDisplayName(plan)}</span>
                {/* A trial's name already says it, so its badge would only repeat it */}
                {state !== PLAN_STATE.TRIAL && <StatusBadge status={planStatusKey(plan)} />}
                {hasCustomLimits(plan, plans.find((item) => item.plan_id === row?.plan_id)) && (
                  <Tooltip title="Custom limits were set for this organisation. Catalog updates don't change them." arrow>
                    <span>
                      <Pill tone="violet">Custom limits</Pill>
                    </span>
                  </Tooltip>
                )}
                {addonLines.length > 0 && (
                  <Tooltip
                    arrow
                    title={
                      <ul className="space-y-0.5 py-0.5 text-xs">
                        {addonSummary.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    }
                  >
                    <span tabIndex={0} aria-label={`Add-ons: ${addonSummary.join(", ")}`} className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black">
                      <Pill tone="green">
                        <FiPackage className="h-3 w-3" aria-hidden="true" />
                        {addonLines.length} {addonLines.length === 1 ? "add-on" : "add-ons"}
                      </Pill>
                    </span>
                  </Tooltip>
                )}
              </div>
              <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs leading-5 text-accent-gray-light">
                {facts.map((fact, index) => (
                  <React.Fragment key={fact.text}>
                    {index > 0 && <span aria-hidden="true">·</span>}
                    {fact.tone ? (
                      <span className={`rounded px-1.5 text-xs font-semibold leading-5 ${FACT_TONES[fact.tone]}`}>{fact.text}</span>
                    ) : fact.hint ? (
                      <Tooltip title={fact.hint} arrow>
                        <span tabIndex={0} aria-label={`${fact.text}: ${fact.hint}`} className="cursor-help border-b border-dotted border-gray-400 font-medium text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-black">
                          {fact.text}
                        </span>
                      </Tooltip>
                    ) : (
                      <span>{fact.text}</span>
                    )}
                  </React.Fragment>
                ))}
              </p>
            </div>

            {state === PLAN_STATE.TRIAL && trialTotal && (
              <TrialRing daysLeft={daysLeft} total={trialTotal} percent={trialPercent} color={trialColor} track={theme.palette.secondary.v2} />
            )}

            {children && <div className="ml-auto flex flex-wrap items-center gap-2">{children}</div>}
          </>
        )}
      </div>
      {!loading && attention && (
        <p className="border-t bg-amber-50 px-5 py-1.5 text-xs text-amber-900" style={{ borderColor: theme.palette.secondary.v4 }}>
          {attention}
        </p>
      )}
      {error && (
        <div className="border-t p-3" style={{ borderColor: theme.palette.secondary.v4 }}>
          <ErrorBox>{error}</ErrorBox>
        </div>
      )}
    </section>
  );
};

export default CurrentPlanCard;
